/**
 * The inbox panel and the popup, mounted by both clients (prompt.md §23, §78).
 *
 * WHY the answer form is the same ClarificationPrompt the conversation renders:
 * a structured answer, a protected credential field, a decline and a snooze must
 * behave identically wherever the question is seen, and a second implementation
 * would be a second place for a credential to leak or a duplicate run to start.
 */

import { ClarificationPrompt, InputRequestList, InputRequestPopup, type InboxRequestView } from "@dayvise/react-ui";

import { useInputRequestInbox, type InputRequestAnswerControls } from "./inputInbox.ts";

function AnswerForm({
  view,
  answers,
  onAnswered,
  refresh,
}: {
  readonly view: InboxRequestView;
  readonly answers: InputRequestAnswerControls;
  readonly onAnswered: () => void;
  readonly refresh: () => void;
}) {
  return (
    <ClarificationPrompt
      // A revised form arrives under the same request id with a new revision, so
      // the revision is part of the identity and the draft is discarded with it.
      key={`${view.requestId}:${view.formRevision ?? ""}`}
      clarification={{
        clarificationId: view.requestId,
        questions: view.questions,
        ...(view.form === null ? {} : { form: view.form, formRevision: view.formRevision ?? undefined }),
      }}
      busy={answers.busy}
      error={answers.error}
      fieldErrors={answers.fieldErrors}
      staleFromServer={answers.stale}
      draftKey={`${view.runId}:${view.requestId}`}
      onSubmit={async (_clarificationId, submitted) => {
        await answers.submitAnswers(view, submitted);
        onAnswered();
      }}
      onSubmitForm={async (submission) => {
        await answers.submitForm(view, submission);
        onAnswered();
      }}
      onDecline={async () => {
        await answers.decline(view);
        onAnswered();
      }}
      onSnooze={() => undefined}
      onRefresh={() => { refresh(); }}
    />
  );
}

export interface InputRequestInboxSectionProps {
  readonly onOpenConversation?: ((conversationId: string) => void) | undefined;
}

/** The durable list of questions still waiting for an answer, with the form for one. */
export function InputRequestInboxSection({ onOpenConversation }: InputRequestInboxSectionProps) {
  const inbox = useInputRequestInbox({ onOpenConversation });
  const selected = inbox.selected;
  return (
    <InputRequestList
      views={inbox.views}
      selectedId={inbox.selectedId}
      loading={inbox.loading}
      error={inbox.error}
      onSelect={(requestId) => inbox.select(inbox.selectedId === requestId ? null : requestId)}
      onRefresh={inbox.refresh}
      onOpenConversation={onOpenConversation}
      notificationStatus={inbox.notifications.status}
      notificationsBusy={inbox.notifications.busy}
      onEnableNotifications={() => { void inbox.notifications.enable(); }}
    >
      {selected === null ? null : (
        <AnswerForm
          view={selected}
          answers={inbox.answers}
          onAnswered={() => inbox.select(null)}
          refresh={inbox.refresh}
        />
      )}
    </InputRequestList>
  );
}

export interface InputRequestPopupHostProps {
  readonly onOpenConversation?: ((conversationId: string) => void) | undefined;
  /** Web raises its own Notification; the desktop shell owns system notifications. */
  readonly desktopNotifications?: boolean;
}

/**
 * The popup that announces one waiting question.
 *
 * It is not a second source of truth: it renders the oldest undismissed request
 * from the durable list, so answering on any other device removes it here without
 * any local message having to arrive.
 */
export function InputRequestPopupHost({ onOpenConversation, desktopNotifications = false }: InputRequestPopupHostProps) {
  const inbox = useInputRequestInbox({ onOpenConversation, desktopNotifications });
  const popup = inbox.popup;
  if (popup === null) return null;
  return (
    <InputRequestPopup
      view={popup}
      pendingCount={inbox.views.filter((view) => view.answerable).length}
      busy={inbox.answers.busy}
      onDismiss={() => inbox.dismissPopup(popup.requestId)}
      onOpenConversation={onOpenConversation}
      notificationStatus={inbox.notifications.status}
      notificationsBusy={inbox.notifications.busy}
      onEnableNotifications={() => { void inbox.notifications.enable(); }}
    >
      <AnswerForm
        view={popup}
        answers={inbox.answers}
        onAnswered={() => inbox.dismissPopup(popup.requestId)}
        refresh={inbox.refresh}
      />
    </InputRequestPopup>
  );
}
